import { Injectable } from '@angular/core';
import { Calendar } from '@awesome-cordova-plugins/calendar/ngx';
import { ClinicDetailQuery } from '../../../shared/state/clinic-detail/clinic-detail.query';
import { Appointment } from '../../../shared/state/appointment/appointment.model';
import { ClinicQuery } from '../../../shared/state/clinic/clinic.query';
import { Platform } from '@ionic/angular';
import { ServicesGroupQuery } from '../../../shared/state/services-group/services-group.query';
import { AppointmentWizardTimeQuery } from '../../../shared/state/appointment-wizard-time/appointment-wizard-time.query';
import { ServiceWizardQuery } from '../../../shared/state/service-wizard/service-wizard.query';
import { AppointmentQuery } from '../../../shared/state/appointment/appointment.query';
import { Capacitor } from '@capacitor/core';

@Injectable({
    providedIn: 'root',
})
export class CalendarDeviceService {
    constructor(
        private calendar: Calendar,
        private clinicDetailQuery: ClinicDetailQuery,
        private clinicQuery: ClinicQuery,
        private platform: Platform,
        private servicesGroupQuery: ServicesGroupQuery,
        private appointmentWizardTimeQuery: AppointmentWizardTimeQuery,
        private serviceWizardQuery: ServiceWizardQuery,
        private appointmentQuery: AppointmentQuery
    ) {}

    async addAppointmentToCalendarDevice(appointmentId: string) {
        if (!Capacitor.isNativePlatform()) {
            return;
        }
        const appointments: Appointment[] = this.createObjectAppointment(
            appointmentId
        );

        /** Por cada cita que hemos creado (puede ser cita multiple), la metemos en el calendar  */
        for (const appointment of appointments) {
            const title =
                this.clinicDetailQuery.comercialName +
                ' (ID#' +
                appointment.id +
                ')';
            /** OJO! Esto puede petar si entramos directamente a la ruta sin que se rellenen las clínicas en Generic
             * (controlador porque solo se usa desde dispositivo y por dispositivo siempre se pasa por buscador) */
            const location = this.clinicDetailQuery.city;
            let notes = 'SERVICIOS\n';
            for (const serviceDescription of appointment.servicesDescription) {
                notes += '- ' + serviceDescription + '\n';
            }

            let startDate;
            let endDate;
            if (this.platform.is('ios')) {
                const {
                    parsedIOSStartDate,
                    parsedIOSEndDate,
                } = CalendarDeviceService.getIosDates(appointment);
                startDate = parsedIOSStartDate;
                endDate = parsedIOSEndDate;
            } else {
                startDate = new Date(
                    appointment.date + ', ' + appointment.timeStart
                );
                endDate = new Date(
                    appointment.date + ', ' + appointment.timeEnd
                );
            }

            await this.calendar.createEvent(
                title,
                location,
                notes,
                startDate,
                endDate
            );
        }
    }

    async deleteAppointmentToCalendarDevice(appointmentId: string) {
        if (!Capacitor.isNativePlatform()) {
            return;
        }
        let appointment: Appointment = this.appointmentQuery.getEntity(
            appointmentId
        );

        let _appointment: Appointment = { ...appointment };
        _appointment.date =
            _appointment.date.split('/')[2] +
            '-' +
            _appointment.date.split('/')[1] +
            '-' +
            _appointment.date.split('/')[0];

        const title =
            this.clinicDetailQuery.comercialName +
            ' (ID#' +
            _appointment.id +
            ')';

        let startDate;
        let endDate;
        if (this.platform.is('ios')) {
            const {
                parsedIOSStartDate,
                parsedIOSEndDate,
            } = CalendarDeviceService.getIosDates(_appointment);
            startDate = parsedIOSStartDate;
            endDate = parsedIOSEndDate;
        } else {
            startDate = new Date(
                _appointment.date + ', ' + _appointment.timeStart
            );
            endDate = new Date(_appointment.date + ', ' + _appointment.timeEnd);
        }

        try {
            await this.calendar.deleteEvent(
                title,
                null,
                null,
                startDate,
                endDate
            );
        } catch (e) {}
    }

    private createObjectAppointment(appointmentId: string): Appointment[] {
        /** Compruebo si estoy haciendo una cita simple o una cita múltiple
         *  puesto que cambia la forma de obtener los datos */

        if (this.servicesGroupQuery.getAll().length === 1) {
            return this.createAppointmentsSingleGroup(appointmentId);
        } else {
            return this.createAppointmentsMultipleGroup(appointmentId);
        }
    }

    private createAppointmentsSingleGroup(
        appointmentId: string
    ): Appointment[] {
        const timeActive = this.appointmentWizardTimeQuery.getActive();

        const servicesChekedArrayDescription = [];
        this.serviceWizardQuery
            .getAll({
                filterBy: ({ checked }) => checked === true,
            })
            .forEach((service) => {
                servicesChekedArrayDescription.push(service.description);
            });

        const singleAppointment = {
            id: appointmentId,
            clinicID: this.clinicDetailQuery.id,
            date: timeActive.date,
            timeStart: timeActive.timeStart,
            timeEnd: timeActive.timeEnd,
            servicesDescription: servicesChekedArrayDescription,
        } as Appointment;

        return [singleAppointment];
    }
    private createAppointmentsMultipleGroup(appointmentId: string) {
        const timeActive: any = this.appointmentWizardTimeQuery.getActive();

        let appointmentsArray = [];

        timeActive.servicesGroup.forEach((serviceGroup, index) => {
            let servicesMultipleGroupDescription = [];
            serviceGroup.ServicesID.split(',').forEach((servicesId) => {
                servicesMultipleGroupDescription.push(
                    this.serviceWizardQuery.getEntity(servicesId).description
                );
            });

            const singleAppointment = {
                id: appointmentId[index],
                clinicID: this.clinicDetailQuery.id,
                date: timeActive.date,
                timeStart: serviceGroup.TimeStart,
                timeEnd: serviceGroup.TimeEnd,
                servicesDescription: servicesMultipleGroupDescription,
            } as Appointment;

            appointmentsArray.push(singleAppointment);
        });

        return appointmentsArray;
    }

    private static getIosDates(appointment: Appointment) {
        const iosDateSplitted = appointment.date.split('-');
        const year = iosDateSplitted[0];
        const month =
            iosDateSplitted[1].length == 1
                ? '0' + iosDateSplitted[1]
                : iosDateSplitted[1];
        const day =
            iosDateSplitted[2].length == 1
                ? '0' + iosDateSplitted[2]
                : iosDateSplitted[2];

        const iosStartDate = new Date(
            year +
                '-' +
                month +
                '-' +
                day +
                'T' +
                appointment.timeStart +
                ':00.000Z'
        );
        const iosEndDate = new Date(
            year +
                '-' +
                month +
                '-' +
                day +
                'T' +
                appointment.timeEnd +
                ':00.000Z'
        );
        const parsedIOSStartDate = CalendarDeviceService.parseIOSDate(
            iosStartDate
        );
        const parsedIOSEndDate = CalendarDeviceService.parseIOSDate(iosEndDate);

        return { parsedIOSStartDate, parsedIOSEndDate };
    }

    private static parseIOSDate(date: Date) {
        const dateSplitted = date.toISOString().split(/[^0-9]/);
        return new Date(
            Number(dateSplitted[0]),
            Number(dateSplitted[1]) - 1,
            Number(dateSplitted[2]),
            Number(dateSplitted[3]),
            Number(dateSplitted[4]),
            Number(dateSplitted[5])
        );
    }
}
